import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiSave, FiSend, FiTrash2, FiClock, FiPlus, FiMove } from 'react-icons/fi';

const STATUSES = ['To Do', 'In Progress', 'QA', 'Done'];
const STATUS_COLORS = { 'To Do': '#6b7280', 'In Progress': '#3b82f6', 'QA': '#8b5cf6', 'Done': '#10b981' };
const PRIORITY_COLORS = { Low: '#6b7280', Medium: '#3b82f6', High: '#f59e0b', Critical: '#ef4444' };
const TYPE_ICONS = { Bug: '🐛', Task: '✅', Story: '📖', Epic: '⚡' };

const TaskDetail = () => {
  const { id, taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminHR = user?.role === 'admin' || user?.role === 'hr';
  const [task, setTask] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [epics, setEpics] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [workLogForm, setWorkLogForm] = useState({ hours: '', minutes: '', description: '', dateLogged: new Date().toISOString().split('T')[0] });
  const [showLogForm, setShowLogForm] = useState(false);
  const [projects, setProjects] = useState([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTargetProject, setMoveTargetProject] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [taskRes, empRes] = await Promise.all([
          api.get(`/tasks/${taskId}`),
          api.get('/employees', { params: { limit: 200 } }),
        ]);
        setTask(taskRes.data);
        setEmployees(empRes.data.employees || []);

        // Fetch epics, stories, and all projects
        const projectId = taskRes.data.project?._id || taskRes.data.project;
        const [projRes] = await Promise.all([
          api.get('/projects'),
        ]);
        setProjects(projRes.data.projects || projRes.data || []);
        if (projectId) {
          const [epicRes, storyRes] = await Promise.all([
            api.get('/tasks', { params: { project: projectId, type: 'Epic', limit: 100 } }),
            api.get('/tasks', { params: { project: projectId, type: 'Story', limit: 100 } }),
          ]);
          setEpics(epicRes.data.tasks || []);
          setStories(storyRes.data.tasks || []);
        }

        setEditForm({
          title: taskRes.data.title,
          description: taskRes.data.description || '',
          type: taskRes.data.type,
          priority: taskRes.data.priority,
          status: taskRes.data.status,
          assignee: taskRes.data.assignee?._id || '',
          dueDate: taskRes.data.dueDate ? new Date(taskRes.data.dueDate).toISOString().split('T')[0] : '',
          parent: taskRes.data.parent?._id || '',
          storyPoints: taskRes.data.storyPoints || '',
          labels: (taskRes.data.labels || []).join(', '),
        });
      } catch (err) {
        toast.error('Failed to load task');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [taskId]);

  const handleSave = async () => {
    try {
      const payload = {
        ...editForm,
        parent: editForm.parent || null,
        storyPoints: editForm.storyPoints ? Number(editForm.storyPoints) : undefined,
        labels: editForm.labels ? editForm.labels.split(',').map(l => l.trim()).filter(Boolean) : [],
      };
      await api.put(`/tasks/${taskId}`, payload);
      // Re-fetch to get populated data
      const refreshed = await api.get(`/tasks/${taskId}`);
      setTask(refreshed.data);
      setEditing(false);
      toast.success('Task updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const res = await api.put(`/tasks/${taskId}/status`, { status });
      setTask({ ...task, ...res.data, status });
      setEditForm(prev => ({ ...prev, status }));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      setSubmitting(true);
      const res = await api.post(`/tasks/${taskId}/comments`, { text: comment });
      setTask(res.data);
      setComment('');
      toast.success('Comment added');
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('Task deleted');
      navigate(`/projects/${id}`);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleLogWork = async (e) => {
    e.preventDefault();
    const hours = parseInt(workLogForm.hours) || 0;
    const minutes = parseInt(workLogForm.minutes) || 0;
    const timeSpent = hours * 60 + minutes;
    if (timeSpent <= 0) { toast.error('Enter time spent'); return; }
    try {
      setSubmitting(true);
      const res = await api.post(`/tasks/${taskId}/worklogs`, {
        timeSpent,
        description: workLogForm.description,
        dateLogged: workLogForm.dateLogged,
      });
      setTask(res.data);
      setWorkLogForm({ hours: '', minutes: '', description: '', dateLogged: new Date().toISOString().split('T')[0] });
      setShowLogForm(false);
      toast.success('Work logged');
    } catch (err) {
      toast.error('Failed to log work');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteWorkLog = async (logId) => {
    if (!window.confirm('Delete this work log?')) return;
    try {
      const res = await api.delete(`/tasks/${taskId}/worklogs/${logId}`);
      setTask(res.data);
      toast.success('Work log deleted');
    } catch (err) {
      toast.error('Failed to delete work log');
    }
  };

  const handleMove = async () => {
    if (!moveTargetProject) { toast.error('Select a project'); return; }
    try {
      const res = await api.put(`/tasks/${taskId}/move`, { targetProjectId: moveTargetProject });
      toast.success(`Task moved to ${res.data.project?.name}`);
      setShowMoveModal(false);
      navigate(`/projects/${moveTargetProject}/tasks/${res.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Move failed');
    }
  };

  const formatTimeSpent = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const totalLogged = (task?.workLogs || []).reduce((sum, wl) => sum + wl.timeSpent, 0);

  if (loading) return <div className="loading">Loading...</div>;
  if (!task) return <div className="loading">Task not found</div>;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="btn-sm btn-secondary" onClick={() => navigate(`/projects/${id}`)}><FiArrowLeft /></button>
          <div>
            <span className="kanban-task-id" style={{ fontSize: '0.9rem' }}>{task.taskId}</span>
            <span className="project-key-badge" style={{ marginLeft: '0.5rem' }}>{task.project?.name}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {!editing ? (
            <button className="btn-primary" onClick={() => setEditing(true)}>Edit</button>
          ) : (
            <button className="btn-primary" onClick={handleSave}><FiSave /> Save</button>
          )}
          {isAdminHR && <button className="btn-sm btn-secondary" onClick={() => { setMoveTargetProject(''); setShowMoveModal(true); }}><FiMove /> Move</button>}
          {isAdminHR && <button className="btn-sm btn-danger" onClick={handleDelete}><FiTrash2 /></button>}
        </div>
      </div>

      {/* Parent Epic Breadcrumb */}
      {task.parent && (
        <div className="task-parent-breadcrumb">
          <span className="task-type-icon">{TYPE_ICONS[task.parent.type] || '⚡'}</span>
          <Link to={`/projects/${id}/tasks/${task.parent._id}`} className="task-parent-link">
            {task.parent.taskId} — {task.parent.title}
          </Link>
        </div>
      )}

      <div className="task-detail-layout">
        {/* Main content */}
        <div className="task-detail-main">
          {editing ? (
            <div className="form-card">
              <div className="form-group">
                <label>Title</label>
                <input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} />
              </div>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label>Description</label>
                <textarea
                  value={editForm.description}
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                  rows={6}
                  placeholder="Add a description..."
                />
              </div>
            </div>
          ) : (
            <div className="form-card">
              <h2 style={{ marginBottom: '1rem' }}>
                <span style={{ marginRight: '0.5rem' }}>{TYPE_ICONS[task.type]}</span>
                {task.title}
              </h2>
              <div className="task-description">
                {task.description || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No description</span>}
              </div>
              {/* Labels */}
              {task.labels && task.labels.length > 0 && (
                <div className="task-labels" style={{ marginTop: '1rem' }}>
                  {task.labels.map((label, i) => (
                    <span key={i} className="task-label-badge">{label}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Child Tasks (for Epics and Stories) */}
          {(task.type === 'Epic' || task.type === 'Story') && task.children && task.children.length > 0 && (
            <div className="form-card" style={{ marginTop: '1rem' }}>
              <h3>{task.type === 'Story' ? 'Tasks in this Story' : 'Child Tasks'} ({task.children.length})</h3>
              <table className="table" style={{ marginTop: '0.75rem' }}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Assignee</th>
                  </tr>
                </thead>
                <tbody>
                  {task.children.map(child => (
                    <tr key={child._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${id}/tasks/${child._id}`)}>
                      <td><span className="kanban-task-id">{child.taskId}</span></td>
                      <td>{TYPE_ICONS[child.type]} {child.title}</td>
                      <td>{child.type}</td>
                      <td>
                        <span className="status-btn active" style={{ '--status-color': STATUS_COLORS[child.status], fontSize: '0.7rem', padding: '2px 8px', cursor: 'default' }}>
                          {child.status}
                        </span>
                      </td>
                      <td>{child.assignee?.name || 'Unassigned'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(task.type === 'Epic' || task.type === 'Story') && (!task.children || task.children.length === 0) && (
            <div className="form-card" style={{ marginTop: '1rem' }}>
              <h3>{task.type === 'Story' ? 'Tasks in this Story' : 'Child Tasks'}</h3>
              <p style={{ color: '#9ca3af', marginTop: '0.75rem', textAlign: 'center' }}>
                {task.type === 'Story'
                  ? 'No tasks yet. Create a Task or Bug and assign it to this Story.'
                  : 'No child tasks yet. Create a Story or Task and link it to this Epic.'}
              </p>
            </div>
          )}

          {/* Sibling Tasks */}
          {task.siblings && task.siblings.length > 0 && (
            <div className="form-card" style={{ marginTop: '1rem' }}>
              <h3>Sibling Tasks ({task.siblings.length})</h3>
              <div className="sibling-tasks-list">
                {task.siblings.map(sib => (
                  <div key={sib._id} className="sibling-task-item" onClick={() => navigate(`/projects/${id}/tasks/${sib._id}`)}>
                    <span className="kanban-task-id">{sib.taskId}</span>
                    <span>{TYPE_ICONS[sib.type]} {sib.title}</span>
                    <span className="status-btn active" style={{ '--status-color': STATUS_COLORS[sib.status], fontSize: '0.65rem', padding: '2px 6px', cursor: 'default' }}>
                      {sib.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className="form-card" style={{ marginTop: '1rem' }}>
            <h3>Comments ({task.comments?.length || 0})</h3>

            <form onSubmit={handleComment} style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
              <input
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Add a comment..."
                style={{ flex: 1 }}
              />
              <button type="submit" className="btn-primary" disabled={submitting || !comment.trim()}>
                <FiSend />
              </button>
            </form>

            <div className="comments-list">
              {(task.comments || []).slice().reverse().map((c, i) => (
                <div key={i} className="comment-item">
                  <div className="comment-header">
                    <span className="comment-avatar">{c.author?.name?.charAt(0).toUpperCase() || '?'}</span>
                    <strong>{c.author?.name || 'Unknown'}</strong>
                    <span className="comment-time">{new Date(c.createdAt).toLocaleString()}</span>
                  </div>
                  <p className="comment-text">{c.text}</p>
                </div>
              ))}
              {(!task.comments || task.comments.length === 0) && (
                <p style={{ color: '#9ca3af', marginTop: '1rem', textAlign: 'center' }}>No comments yet</p>
              )}
            </div>
          </div>

          {/* Work Logs Section */}
          <div className="form-card" style={{ marginTop: '1rem' }}>
            <div className="worklog-header">
              <h3><FiClock style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />Work Logs</h3>
              <div className="worklog-summary">
                <span className="worklog-total-badge">{formatTimeSpent(totalLogged)} logged</span>
                <button className="btn-sm btn-primary" onClick={() => setShowLogForm(!showLogForm)}>
                  <FiPlus /> Log Work
                </button>
              </div>
            </div>

            {showLogForm && (
              <form onSubmit={handleLogWork} className="worklog-form">
                <div className="worklog-time-inputs">
                  <div className="worklog-time-field">
                    <label>Hours</label>
                    <input
                      type="number"
                      min="0"
                      max="24"
                      value={workLogForm.hours}
                      onChange={e => setWorkLogForm({ ...workLogForm, hours: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="worklog-time-field">
                    <label>Minutes</label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={workLogForm.minutes}
                      onChange={e => setWorkLogForm({ ...workLogForm, minutes: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="worklog-time-field">
                    <label>Date</label>
                    <input
                      type="date"
                      value={workLogForm.dateLogged}
                      onChange={e => setWorkLogForm({ ...workLogForm, dateLogged: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: '0.5rem' }}>
                  <input
                    value={workLogForm.description}
                    onChange={e => setWorkLogForm({ ...workLogForm, description: e.target.value })}
                    placeholder="What did you work on?"
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button type="submit" className="btn-primary" disabled={submitting}>Log</button>
                  <button type="button" className="btn-sm btn-secondary" onClick={() => setShowLogForm(false)}>Cancel</button>
                </div>
              </form>
            )}

            <div className="worklog-list">
              {(task.workLogs || []).slice().reverse().map((wl) => (
                <div key={wl._id} className="worklog-item">
                  <div className="worklog-item-header">
                    <span className="comment-avatar">{wl.author?.name?.charAt(0).toUpperCase() || '?'}</span>
                    <div className="worklog-item-meta">
                      <strong>{wl.author?.name || 'Unknown'}</strong>
                      <span className="worklog-time-badge">{formatTimeSpent(wl.timeSpent)}</span>
                    </div>
                    <span className="comment-time">{new Date(wl.dateLogged).toLocaleDateString()}</span>
                    {(wl.author?._id === user?._id || isAdminHR) && (
                      <button className="worklog-delete" onClick={() => handleDeleteWorkLog(wl._id)} title="Delete">
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                  {wl.description && <p className="worklog-description">{wl.description}</p>}
                </div>
              ))}
              {(!task.workLogs || task.workLogs.length === 0) && (
                <p style={{ color: '#9ca3af', marginTop: '1rem', textAlign: 'center' }}>No work logged yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="task-detail-sidebar">
          <div className="form-card">
            {/* Status */}
            <div className="task-sidebar-field">
              <label>Status</label>
              <div className="status-buttons">
                {STATUSES.map(s => (
                  <button
                    key={s}
                    className={`status-btn ${task.status === s ? 'active' : ''}`}
                    style={{ '--status-color': STATUS_COLORS[s] }}
                    onClick={() => handleStatusChange(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Type */}
            <div className="task-sidebar-field">
              <label>Type</label>
              {editing ? (
                <select value={editForm.type} onChange={e => setEditForm({ ...editForm, type: e.target.value })}>
                  {['Bug', 'Task', 'Story', 'Epic'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              ) : (
                <span>{TYPE_ICONS[task.type]} {task.type}</span>
              )}
            </div>

            {/* Priority */}
            <div className="task-sidebar-field">
              <label>Priority</label>
              {editing ? (
                <select value={editForm.priority} onChange={e => setEditForm({ ...editForm, priority: e.target.value })}>
                  {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              ) : (
                <span style={{ color: PRIORITY_COLORS[task.priority], fontWeight: 600 }}>{task.priority}</span>
              )}
            </div>

            {/* Parent (context-aware: Story for Task/Bug, Epic for Story) */}
            {task.type !== 'Epic' && (
              <div className="task-sidebar-field">
                <label>
                  {(editForm.type || task.type) === 'Story' ? 'Parent Epic' : 'Parent Story'}
                </label>
                {editing ? (
                  <select value={editForm.parent} onChange={e => setEditForm({ ...editForm, parent: e.target.value })}>
                    <option value="">None</option>
                    {(editForm.type === 'Story' ? epics : stories)
                      .filter(t => t._id !== taskId)
                      .map(t => (
                        <option key={t._id} value={t._id}>{t.taskId} — {t.title}</option>
                      ))}
                  </select>
                ) : (
                  <span>
                    {task.parent ? (
                      <Link to={`/projects/${id}/tasks/${task.parent._id}`} className="task-parent-link" style={{ fontSize: '0.85rem' }}>
                        {TYPE_ICONS[task.parent.type] || ''} {task.parent.taskId} — {task.parent.title}
                      </Link>
                    ) : 'None'}
                  </span>
                )}
              </div>
            )}

            {/* Assignee */}
            <div className="task-sidebar-field">
              <label>Assignee</label>
              {editing ? (
                <select value={editForm.assignee} onChange={e => setEditForm({ ...editForm, assignee: e.target.value })}>
                  <option value="">Unassigned</option>
                  {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
                </select>
              ) : (
                <span>{task.assignee?.name || 'Unassigned'}</span>
              )}
            </div>

            {/* Reporter */}
            <div className="task-sidebar-field">
              <label>Reporter</label>
              <span>{task.reporter?.name || '-'}</span>
            </div>

            {/* Story Points */}
            <div className="task-sidebar-field">
              <label>Story Points</label>
              {editing ? (
                <input
                  type="number"
                  min="0"
                  value={editForm.storyPoints}
                  onChange={e => setEditForm({ ...editForm, storyPoints: e.target.value })}
                  placeholder="e.g. 3"
                />
              ) : (
                <span>{task.storyPoints || '-'}</span>
              )}
            </div>

            {/* Labels */}
            <div className="task-sidebar-field">
              <label>Labels</label>
              {editing ? (
                <input
                  value={editForm.labels}
                  onChange={e => setEditForm({ ...editForm, labels: e.target.value })}
                  placeholder="frontend, backend, urgent"
                />
              ) : (
                <span>
                  {task.labels && task.labels.length > 0
                    ? task.labels.map((l, i) => <span key={i} className="task-label-badge" style={{ marginRight: '4px' }}>{l}</span>)
                    : '-'
                  }
                </span>
              )}
            </div>

            {/* Due Date */}
            <div className="task-sidebar-field">
              <label>Due Date</label>
              {editing ? (
                <input type="date" value={editForm.dueDate} onChange={e => setEditForm({ ...editForm, dueDate: e.target.value })} />
              ) : (
                <span className={task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'Done' ? 'overdue-text' : ''}>
                  {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
                </span>
              )}
            </div>

            {/* Time Logged */}
            <div className="task-sidebar-field">
              <label>Time Logged</label>
              <span style={{ fontWeight: 600, color: totalLogged > 0 ? '#4f46e5' : '#9ca3af' }}>
                {totalLogged > 0 ? formatTimeSpent(totalLogged) : '-'}
              </span>
            </div>

            {/* Timestamps */}
            <div className="task-sidebar-field">
              <label>Created</label>
              <span className="task-timestamp">{new Date(task.createdAt).toLocaleString()}</span>
            </div>
            <div className="task-sidebar-field">
              <label>Updated</label>
              <span className="task-timestamp">{new Date(task.updatedAt).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Move to Project Modal */}
      {showMoveModal && (
        <div className="modal-overlay" onClick={() => setShowMoveModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem' }}><FiMove style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />Move Task to Project</h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
              Moving <strong>{task.taskId}</strong> will assign it a new task ID in the destination project and clear its parent link.
            </p>
            <div className="form-group">
              <label>Destination Project</label>
              <select value={moveTargetProject} onChange={e => setMoveTargetProject(e.target.value)}>
                <option value="">Select project...</option>
                {projects
                  .filter(p => p._id !== (task.project?._id || task.project))
                  .map(p => <option key={p._id} value={p._id}>{p.name} ({p.key})</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setShowMoveModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleMove} disabled={!moveTargetProject}>Move Task</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetail;
